import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Menu, LogOut, MessageSquare, Brain, ShieldCheck } from "lucide-react";
import { useState } from "react";
import ConciergeWidget from "@/components/ConciergeWidget";

export default function Header() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const links = user?.role === "coach"
    ? [
        { to: "/coach", label: "Dashboard" },
        { to: "/coach/profile", label: "Profile" },
        { to: "/coach/opportunities", label: "Opportunities" },
        { to: "/coach/jobs", label: "Job Search" },
        { to: "/coach/alerts", label: "Alerts" },
        { to: "/cir", label: "CIR" },
        { to: "/messages", label: "Messages" },
        { to: "/who-we-are", label: "Who We Are" },
          { to: "/pricing", label: "Pricing" },
          { to: "/jobs/weekly", label: "Openings" },
      ]
    : user?.role === "athletic_director"
      ? [
          { to: "/ad", label: "Dashboard" },
          { to: "/ad/jobs/new", label: "Post Job" },
          { to: "/ad/stealth", label: "Stealth" },
          { to: "/ad/talent", label: "Talent Pool" },
          { to: "/ad/invites", label: "Invites" },
          ...(user?.is_platform_admin ? [{ to: "/ad/marketing", label: "Admin HQ", admin: true }] : []),
          { to: "/ad/market", label: "Market Intel" },
          { to: "/messages", label: "Messages" },
          { to: "/who-we-are", label: "Who We Are" },
          { to: "/pricing", label: "Pricing" },
          { to: "/jobs/weekly", label: "Openings" },
        ]
      : [
          { to: "/cir", label: "CIR" },
          { to: "/who-we-are", label: "Who We Are" },
          { to: "/pricing", label: "Pricing" },
          { to: "/jobs/weekly", label: "Openings" },
        ];

  const onLogout = () => { logout(); nav("/"); };

  return (
    <header className="sticky top-0 z-50 bg-appbg/90 backdrop-blur-xl border-b-2 border-blaze">
      {/* Team-color strip — red-to-blue gradient anchors every page in the app palette */}
      <div className="h-2 w-full" style={{ background: "linear-gradient(90deg, rgb(var(--app-primary-rgb)) 0%, rgb(var(--app-primary-rgb)) 40%, rgb(var(--app-accent-rgb)) 100%)" }} />
      <div className="px-4 md:px-8 lg:px-12 py-4 flex items-center justify-between">
        <Link to={user ? (user.role === "coach" ? "/coach" : "/ad") : "/"} className="flex items-center gap-2" data-testid="header-logo-link">
          <Brain className="text-blaze" size={22} strokeWidth={2} />
          <span className="heading text-2xl">CoachConnect <span className="text-blaze">AI</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2 text-sm uppercase tracking-widest font-medium transition-colors ${
                l.admin
                  ? `flex items-center gap-1.5 ${loc.pathname === l.to ? "text-amber-300 border-b-2 border-amber-400" : "text-amber-500/80 hover:text-amber-300"}`
                  : loc.pathname === l.to ? "text-white border-b-2 border-blaze" : "text-neutral-400 hover:text-white"
              }`}
              title={l.admin ? "Admin-only (back office)" : undefined}
            >
              {l.admin && <ShieldCheck size={13} className="shrink-0" />}
              {l.label}
            </Link>
          ))}
          {user ? (
            <button
              onClick={onLogout}
              data-testid="header-logout-btn"
              className="ml-4 flex items-center gap-2 px-4 py-2 text-sm uppercase tracking-widest text-neutral-400 hover:text-blaze transition-colors"
            >
              <LogOut size={16} /> Logout
            </button>
          ) : (
            <Link to="/auth" data-testid="header-auth-link" className="ml-4 px-6 py-2 bg-blaze text-white heading text-lg tracking-wider hover:glow-blaze transition-all">
              Sign in
            </Link>
          )}
        </nav>

        <button className="md:hidden" data-testid="header-menu-btn" onClick={() => setOpen(!open)}>
          <Menu size={22} />
        </button>
      </div>

      {user?.role === "athletic_director" && <ConciergeWidget />}
      {open && (
        <div className="md:hidden border-t border-neutral-800 px-4 py-3 space-y-1 bg-appbg">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-2 py-2 text-sm uppercase tracking-widest ${
                l.admin ? "flex items-center gap-1.5 text-amber-400 hover:text-amber-300" : "block text-neutral-300 hover:text-blaze"
              }`}
            >
              {l.admin && <ShieldCheck size={13} className="shrink-0" />}
              {l.label}
            </Link>
          ))}
          {user ? (
            <button
              onClick={onLogout}
              data-testid="mobile-logout-btn"
              className="block w-full text-left px-2 py-2 text-sm uppercase tracking-widest text-neutral-300 hover:text-blaze"
            >
              Logout
            </button>
          ) : (
            <Link to="/auth" data-testid="mobile-auth-link" className="block px-2 py-2 text-sm uppercase tracking-widest text-blaze">
              Sign in
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
